import { useState } from "react";
import { apiFetch } from "../../../context/AuthContext";

const useDepartments = ({ BASE_URL, profile, selectedCompanyName, setConfig, showAlert, isRTL }) => {
  const [departments, setDepartments] = useState([]);
  const [workingCompanies, setWorkingCompanies] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  const loadDepartmentRules = async (departmentsList) => {
    if (!selectedCompanyName || departmentsList.length === 0) return;

    const clientName = profile?.clientName || localStorage.getItem("clientName");
    const departmentRulesMap = {};

    // Fetch all department rules in parallel for better performance
    const results = await Promise.all(
      departmentsList.map(async (dept) => {
        try {
          const response = await apiFetch(
            `${BASE_URL}/api/company-config/${clientName}/${encodeURIComponent(
              selectedCompanyName
            )}/departments/${encodeURIComponent(dept)}`,
            {
              method: "GET",
              headers: { "Content-Type": "application/json" },
            }
          );

          if (response.ok) {
            const data = await response.json();
            if (data.success && data.data.rules) {
              console.log(`✅ Loaded rules for department: ${dept}`);
              return { dept, rules: data.data.rules };
            }
          }
        } catch (error) {
          console.error(`❌ Error loading rules for ${dept}:`, error);
        }
        return null;
      })
    );

    results.forEach((result) => {
      if (result) {
        departmentRulesMap[result.dept] = result.rules;
      }
    });

    setConfig((prev) => ({ ...prev, departmentRules: departmentRulesMap }));
  };

  const fetchDepartmentsAndCompanies = async () => {
    if (!selectedCompanyName) return;

    try {
      setLoadingDepartments(true);
      const clientName = profile?.clientName || localStorage.getItem("clientName");

      console.log(`📋 Fetching departments for company: ${selectedCompanyName}`);

      try {
        const deptResponse = await apiFetch(
          `${BASE_URL}/api/company-config/${clientName}/${encodeURIComponent(selectedCompanyName)}/departments-list`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }
        );

        if (deptResponse.ok) {
          const deptData = await deptResponse.json();

          if (deptData.success && !deptData.data.accessDenied) {
            const departments = deptData.data.departments || [];

            console.log(`✅ Fetched ${departments.length} departments from backend`);
            console.log(`🔒 Has visibility restrictions:`, deptData.data.hasVisibilityRestrictions);

            setDepartments(departments);

            const empResponse = await apiFetch(
              `${BASE_URL}/employees/clients/${clientName}`,
              {
                method: "GET",
                headers: { "Content-Type": "application/json" },
              }
            );

            if (empResponse.ok) {
              const empData = await empResponse.json();
              const employees = (empData.employees || []).filter(
                (emp) => emp.workingCompany === selectedCompanyName
              );

              const uniqueWorkingCompanies = [
                ...new Set(
                  employees
                    .map((emp) => emp.workingCompany)
                    .filter((company) => company && company.trim() !== "")
                ),
              ].sort();

              setWorkingCompanies(uniqueWorkingCompanies);
            }

            await loadDepartmentRules(departments);

            setLoadingDepartments(false);
            return;
          } else if (deptData.data.accessDenied) {
            console.warn(`⚠️ Access denied to company: ${selectedCompanyName}`);
            setDepartments([]);
            setWorkingCompanies([]);
            setLoadingDepartments(false);
            showAlert(
              isRTL ? "تحذير" : "Warning",
              isRTL ? "ليس لديك صلاحيات للوصول إلى هذه الشركة" : "You don't have access to this company",
              "warning"
            );
            return;
          }
        }
      } catch (backendError) {
        console.warn("⚠️ Backend department endpoint failed, falling back to client-side filtering:", backendError);
      }

      const response = await apiFetch(
        `${BASE_URL}/employees/clients/${clientName}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const allEmployees = data.employees || [];

        const employees = allEmployees.filter(
          (emp) => emp.workingCompany === selectedCompanyName
        );

        console.log(`✅ Fetched ${employees.length} employees for company ${selectedCompanyName}`);

        let uniqueDepartments = [
          ...new Set(
            employees
              .map((emp) => emp.employeeType)
              .filter((type) => type && type.trim() !== "")
          ),
        ].sort();

        if (window.departmentRestrictions && window.departmentRestrictions[selectedCompanyName]) {
          const allowedDepts = window.departmentRestrictions[selectedCompanyName];

          if (allowedDepts !== "ALL") {
            console.log(`🔒 Applying department filter for ${selectedCompanyName}:`, allowedDepts);
            uniqueDepartments = uniqueDepartments.filter((dept) => allowedDepts.includes(dept));
            console.log(`✓ Filtered departments:`, uniqueDepartments);
          } else {
            console.log(`✓ ALL departments allowed for ${selectedCompanyName}`);
          }
        }

        const uniqueWorkingCompanies = [
          ...new Set(
            employees
              .map((emp) => emp.workingCompany)
              .filter((company) => company && company.trim() !== "")
          ),
        ].sort();

        console.log("📋 Unique Departments:", uniqueDepartments);
        console.log("🏢 Unique Working Companies:", uniqueWorkingCompanies);

        setDepartments(uniqueDepartments);
        setWorkingCompanies(uniqueWorkingCompanies);

        await loadDepartmentRules(uniqueDepartments);
      } else {
        console.error("❌ Failed to fetch employees:", response.status);
      }
    } catch (error) {
      console.error("❌ Error fetching departments:", error);
      showAlert(
        isRTL ? "خطأ" : "Error",
        isRTL ? "فشل في جلب الأقسام" : "Failed to fetch departments",
        "error"
      );
    } finally {
      setLoadingDepartments(false);
    }
  };

  return {
    departments,
    workingCompanies,
    loadingDepartments,
    fetchDepartmentsAndCompanies,
    loadDepartmentRules,
  };
};

export default useDepartments;